import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Pencil, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import type { WeedingRecord, BookCopy, ColumnFilterOption } from "@/types";
import { formatGridDate } from "@/lib/utils";

export default function WeedingRegisterPage() {
  const { api, user } = useAuth();
  const [records, setRecords] = useState<WeedingRecord[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin" || user?.role === "librarian";

  const [copySearch, setCopySearch] = useState("");
  const [copies, setCopies] = useState<BookCopy[]>([]);
  const [selectedCopy, setSelectedCopy] = useState<BookCopy | null>(null);
  const [reason, setReason] = useState("");

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<WeedingRecord | null>(null);
  const [editReason, setEditReason] = useState("");

  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      const textParts = [columnFilters.book_title, columnFilters.accession_no].filter(Boolean);
      if (textParts.length) params.search = textParts.join(" ");
      if (columnFilters.approval_status) params.approval_status = columnFilters.approval_status;
      const res = await api.get("/weeding", { params });
      setRecords(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [api, page, columnFilters]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const searchCopies = useCallback(
    async (q: string) => {
      if (q.length < 2) {
        setCopies([]);
        return;
      }
      try {
        const res = await api.get("/books", { params: { search: q, limit: 5 } });
        const allCopies: any[] = [];
        for (const b of res.data.items) {
          const c = await api.get(`/books/${b.id}/copies`);
          c.data.forEach((copy: any) => allCopies.push({ ...copy, book_title: b.title }));
        }
        setCopies(allCopies);
      } catch {}
    },
    [api],
  );

  const handleCopySearchChange = (v: string) => {
    setCopySearch(v);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => searchCopies(v), 300);
  };

  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    setColumnFilters(filters);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, []);

  const submitWeeding = async () => {
    if (!selectedCopy || !reason) {
      toast.error("Select a copy and provide reason");
      return;
    }
    try {
      await api.post("/weeding", { book_copy_id: selectedCopy.id, reason });
      toast.success("Weeding request created");
      setSelectedCopy(null);
      setCopies([]);
      setCopySearch("");
      setReason("");
      fetchRecords();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const approveWeeding = async (id: string) => {
    try {
      await api.put(`/weeding/${id}`, { approval_status: "approved" });
      toast.success("Approved");
      fetchRecords();
    } catch {
      toast.error("Failed");
    }
  };

  const openEditRecord = (record: WeedingRecord) => {
    setEditingRecord(record);
    setEditReason(record.reason || "");
    setEditDialogOpen(true);
  };

  const saveEditRecord = async () => {
    if (!editingRecord) return;
    try {
      await api.put(`/weeding/${editingRecord.id}`, { reason: editReason });
      toast.success("Updated");
      setEditDialogOpen(false);
      fetchRecords();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const deleteRecord = async (id: string) => {
    if (!window.confirm("Delete this weeding record?")) return;
    try {
      await api.delete(`/weeding/${id}`);
      toast.success("Deleted");
      fetchRecords();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const approvalStatusOptions: ColumnFilterOption[] = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];

  const columns: any[] = [
    { accessorKey: "book_title", header: "Book", meta: { filterVariant: "text", filterPlaceholder: "Book..." } },
    {
      accessorKey: "accession_no",
      header: "Accession No",
      meta: { filterVariant: "text", filterPlaceholder: "Accession..." },
      cell: ({ row }: any) => <span className="font-mono text-xs">{row.original.accession_no}</span>,
    },
    { accessorKey: "reason", header: "Reason" },
    {
      accessorKey: "approval_status",
      header: "Status",
      meta: { filterVariant: "select", filterOptions: approvalStatusOptions, filterPlaceholder: "Status" },
      cell: ({ row }: any) => {
        const s = row.original.approval_status;
        const cls = s === "approved" ? "bg-green-100 text-green-800" : s === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800";
        return <Badge className={`${cls} hover:${cls}`}>{s}</Badge>;
      },
    },
    {
      accessorKey: "created_at",
      header: "Date",
      meta: { filterVariant: "date-range" },
      cell: ({ row }: any) => formatGridDate(row.original.created_at),
    },
    ...(isAdmin
      ? [
          {
            id: "actions",
            header: "Actions",
            cell: ({ row }: any) => (
              <div className="flex items-center gap-1" onClick={(e: any) => e.stopPropagation()}>
                {row.original.approval_status === "pending" && (
                  <Button size="sm" className="h-7 bg-primary hover:bg-primary/90" onClick={() => approveWeeding(row.original.id)}>
                    <CheckCircle className="w-4 h-4 mr-1" /> Approve
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditRecord(row.original)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteRecord(row.original.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6" data-testid="weeding-register-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Weeding Register
        </h1>
      </div>

      {isAdmin && (
        <Card className="border-0 shadow-sm" data-testid="weeding-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-primary" /> New Weeding Request
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                value={copySearch}
                onChange={(e) => handleCopySearchChange(e.target.value)}
                placeholder="Search book to weed (type 2+ letters)"
                data-testid="weeding-search-input"
              />
            </div>
            {copies.length > 0 && !selectedCopy && (
              <div className="border rounded-md bg-card max-h-40 overflow-y-auto">
                {copies.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setSelectedCopy(c);
                      setCopies([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b last:border-0"
                  >
                    <span className="font-medium">{c.book_title}</span>
                    <span className="font-mono text-xs ml-2">{c.accession_no}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedCopy && (
              <div className="p-2 bg-primary/5 rounded-md flex items-center gap-2">
                <Badge className="bg-primary text-white hover:bg-primary">{selectedCopy.book_title}</Badge>
                <span className="font-mono text-xs">{selectedCopy.accession_no}</span>
                <Button variant="ghost" size="sm" onClick={() => setSelectedCopy(null)} className="ml-auto h-7 text-xs">
                  Change
                </Button>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Reason *</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for weeding"
                rows={2}
                data-testid="weeding-reason-input"
              />
            </div>
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={submitWeeding}
              disabled={!selectedCopy || !reason}
              data-testid="weeding-submit-btn"
            >
              Submit Request
            </Button>
          </CardContent>
        </Card>
      )}

      <DataTable
        columns={columns}
        data={records}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        onRowClick={isAdmin ? openEditRecord : undefined}
        data-testid="weeding-table"
      />

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Weeding Record</DialogTitle>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-4">
              <div className="p-2 bg-muted rounded text-sm">
                <span className="font-medium">{editingRecord.book_title}</span> —{" "}
                <span className="font-mono text-xs">{editingRecord.accession_no}</span>
              </div>
              <div className="space-y-1.5">
                <Label>Reason</Label>
                <Textarea value={editReason} onChange={(e) => setEditReason(e.target.value)} rows={3} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={saveEditRecord}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
